/*
  GEMDRAW — Reveal, parallax and tilt engine

  One engine for every motion behavior the sections opt into through data
  attributes, rather than a script per section:

    data-gd-reveal="up|left|right|fade|scale"   entrance
    data-gd-reveal="clip|mask"                  editorial and campaign entrances
    data-gd-stagger                             stagger this element's children
    data-gd-stagger-step="90"                   override the step, in ms
    data-gd-reveal-delay="240"                  fixed delay, in ms
    data-gd-reveal-now                          reveal on load, not on scroll
    data-gd-border-draw="right|down"            rule that draws on reveal
    data-gd-parallax="10"                       scroll parallax, in px
    data-gd-tilt="4"                            pointer tilt, in degrees
    data-gd-tilt-self                           component owns its own transform
    data-gd-sheen                               publish pointer position as %

  Three rules shape the file:

  - Motion never gates content. The starting state lives behind .gd-motion on
    the root element, set by an inline script only when scripting is available
    and reduced motion is not requested. If this file never runs, every element
    is already in its final state.
  - One observer and one scroll loop for the whole page, not one per element.
    The loop only runs while something parallaxed is actually on screen.
  - Everything is idempotent and re-entrant, because the theme editor
    re-renders sections in place and would otherwise double-bind them.
*/

(function () {
  'use strict';

  const ROOT = document.documentElement;
  const reduceQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
  const pointerQuery = window.matchMedia('(hover: hover) and (pointer: fine)');
  const desktopQuery = window.matchMedia('(min-width: 990px)');

  function motionAllowed() {
    return !reduceQuery.matches;
  }

  /* Reveals ----------------------------------------------------------------- */

  let observer = null;

  function markRevealed(el) {
    el.setAttribute('data-gd-revealed', '');
  }

  /*
    Children of a staggered container get an increasing delay. The delay is
    written once, when the container is prepared, so the observer callback stays
    cheap and nothing recalculates during the reveal itself.
  */
  function prepareStagger(container) {
    const step = parseInt(container.getAttribute('data-gd-stagger-step'), 10) || 70;
    const base = parseInt(container.getAttribute('data-gd-reveal-delay'), 10) || 0;
    let index = 0;

    Array.prototype.forEach.call(container.children, function (child) {
      if (!child.hasAttribute('data-gd-reveal')) return;
      /* Capped so a long list cannot end with a child waiting a full second. */
      const delay = base + Math.min(index, 8) * step;
      child.style.setProperty('--gd-reveal-delay', delay + 'ms');
      index += 1;
    });
  }

  function prepareDelay(el) {
    const delay = parseInt(el.getAttribute('data-gd-reveal-delay'), 10);
    if (!Number.isNaN(delay) && delay > 0) {
      el.style.setProperty('--gd-reveal-delay', delay + 'ms');
    }
  }

  /*
    Elements observed but not yet revealed.

    The observer alone is not enough. It reports threshold crossings, and an
    instant jump — an anchor link, a restored scroll position, End, or a script
    setting scrollTop — can take an element from below the viewport to above it
    without ever intersecting. No crossing, no callback, and the element stays
    at opacity zero with its content unreachable.

    The sweep below closes that hole: one rAF-throttled pass that reveals
    anything already at or above the reveal line. It detaches itself the moment
    nothing is left pending, so a fully revealed page pays nothing for it.
  */
  const pending = new Set();
  let sweepFrame = null;
  let sweepListening = false;

  function reveal(el) {
    pending.delete(el);
    if (observer) observer.unobserve(el);
    markRevealed(el);
  }

  function sweep() {
    sweepFrame = null;

    if (pending.size) {
      const line = window.innerHeight * 0.88;
      Array.from(pending).forEach(function (el) {
        if (!document.contains(el)) {
          pending.delete(el);
          return;
        }
        if (el.getBoundingClientRect().top < line) reveal(el);
      });
    }

    if (!pending.size && sweepListening) {
      window.removeEventListener('scroll', scheduleSweep);
      window.removeEventListener('resize', scheduleSweep);
      sweepListening = false;
    }
  }

  function scheduleSweep() {
    if (sweepFrame !== null) return;
    sweepFrame = window.requestAnimationFrame(sweep);
  }

  function watchSweep() {
    if (sweepListening) return;
    sweepListening = true;
    window.addEventListener('scroll', scheduleSweep, { passive: true });
    window.addEventListener('resize', scheduleSweep, { passive: true });
  }

  function getObserver() {
    if (observer) return observer;

    observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) reveal(entry.target);
        });
      },
      /* Fires a little before the element is fully in view, so the entrance
         finishes about when the reader arrives at it. */
      { rootMargin: '0px 0px -12% 0px', threshold: 0.01 }
    );

    return observer;
  }

  function bindReveals(scope) {
    const targets = scope.querySelectorAll('[data-gd-reveal], [data-gd-border-draw]');
    if (!targets.length) return;

    /* Without motion, or without IntersectionObserver, everything is simply
       already revealed. The page is never left mid-animation. */
    if (!motionAllowed() || !('IntersectionObserver' in window)) {
      Array.prototype.forEach.call(targets, markRevealed);
      return;
    }

    scope.querySelectorAll('[data-gd-stagger]').forEach(prepareStagger);

    const io = getObserver();

    Array.prototype.forEach.call(targets, function (el) {
      if (el.hasAttribute('data-gd-revealed')) return;
      if (el.dataset.gdRevealBound === 'true') return;
      el.dataset.gdRevealBound = 'true';

      if (!el.closest('[data-gd-stagger]')) prepareDelay(el);

      /* Above the fold on load: reveal on the next frame instead of waiting for
         a scroll that may never happen. */
      if (el.hasAttribute('data-gd-reveal-now')) {
        window.requestAnimationFrame(function () {
          window.requestAnimationFrame(function () {
            markRevealed(el);
          });
        });
        return;
      }

      pending.add(el);
      io.observe(el);
    });

    if (pending.size) {
      watchSweep();
      /* Covers a page opened directly at an anchor, where the target is already
         past the reveal line before any scroll happens. */
      scheduleSweep();
    }
  }

  /* Parallax ---------------------------------------------------------------- */

  /*
    A single loop for every parallaxed element, driven by scroll but throttled
    to one rAF frame. It does no work at all while nothing is on screen, so a
    long page does not pay for a hero it has scrolled past.
  */
  const parallaxItems = [];
  let parallaxFrame = null;
  let parallaxObserver = null;

  function applyParallax() {
    parallaxFrame = null;
    const viewportH = window.innerHeight;

    parallaxItems.forEach(function (item) {
      if (!item.visible) return;
      const rect = item.el.getBoundingClientRect();
      /* -1 at the bottom of the viewport, +1 at the top. */
      const progress = (viewportH - rect.top) / (viewportH + rect.height) - 0.5;
      const offset = (progress * -2 * item.distance).toFixed(2);
      item.el.style.setProperty('--gd-parallax-y', offset + 'px');
    });
  }

  function scheduleParallax() {
    if (parallaxFrame !== null) return;
    parallaxFrame = window.requestAnimationFrame(applyParallax);
  }

  function getParallaxObserver() {
    if (parallaxObserver) return parallaxObserver;

    parallaxObserver = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        const item = parallaxItems.find(function (candidate) {
          return candidate.el === entry.target;
        });
        if (item) item.visible = entry.isIntersecting;
      });
      scheduleParallax();
    });

    return parallaxObserver;
  }

  /*
    PARALLAX RUNS ON A PHONE TOO.

    It was gated behind a 990px breakpoint on the assumption that scroll-linked
    motion is a desktop luxury. It is not: parallax is driven by scroll position
    and a phone scrolls, so the effect works identically — and withholding it
    was the single biggest reason the mobile page felt flatter than the desktop
    one.

    The cost is already bounded by the loop itself: one rAF-throttled listener
    for the whole page, doing no work at all while nothing parallaxed is on
    screen. That budget does not change with viewport width.

    Reduced motion still turns it off entirely.
  */
  function bindParallax(scope) {
    if (!motionAllowed() || !('IntersectionObserver' in window)) return;

    scope.querySelectorAll('[data-gd-parallax]').forEach(function (el) {
      if (el.dataset.gdParallaxBound === 'true') return;
      el.dataset.gdParallaxBound = 'true';

      /* Clamped to the approved envelope: enough to separate the planes, never
         enough to read as the page sliding apart. */
      const requested = parseFloat(el.getAttribute('data-gd-parallax')) || 10;
      const distance = Math.max(4, Math.min(12, requested));

      parallaxItems.push({ el: el, distance: distance, visible: false });
      getParallaxObserver().observe(el);
    });

    if (parallaxItems.length && !window.__gdParallaxListening) {
      window.__gdParallaxListening = true;
      window.addEventListener('scroll', scheduleParallax, { passive: true });
      window.addEventListener('resize', scheduleParallax, { passive: true });
    }
  }

  /* Tilt and specular ------------------------------------------------------- */

  /*
    Restrained pointer tilt, and the specular highlight that goes with it.

    Pointer devices only, desktop only, never under reduced motion — on a touch
    screen it would either never fire or fire on tap, and neither is useful.

    ONE HANDLER, FOUR CUSTOM PROPERTIES. An element can ask for the tilt
    (data-gd-tilt), the pointer position (data-gd-sheen), or both, and either way
    it costs a single pointermove listener writing into custom properties:

      --gd-tilt-x / --gd-tilt-y   rotation, in degrees
      --gd-pointer-x / -y         pointer position within the element, in %

    The component decides what to do with them. Nothing here writes a transform
    string or touches a class, so a card can compose the tilt with its own
    hover lift and scale in one declaration instead of fighting this file for
    the transform property.

    THE TWO AXES ARE NOT EQUAL. Vertical rotation reads as the card turning to
    face you and horizontal rotation reads as it tipping away, and the second
    tolerates far less before the card looks like it is flipping. So rotateX is
    held at three quarters of the requested maximum: a card asking for 4 gets
    ±4deg of turn and ±3deg of tip, which is the approved envelope.

    Writes are batched through one rAF frame, so a fast pointer across a row of
    cards cannot schedule more style work than the compositor can take.
  */
  /*
    TOUCH GETS THE SAME EFFECT, DRIVEN BY THE ONLY INPUT IT HAS.

    Pointer tilt cannot work on a touch screen in the way it does with a mouse:
    there is no hover, so there is no continuous position to read. Gating the
    whole feature behind a fine pointer was correct for the tilt itself and
    wrong for everything attached to it — the packs, the campaign panels and the
    product cards all simply went inert on a phone.

    So on a coarse pointer the same element is bound to touch instead. A finger
    down publishes the position it landed on and the card leans toward it; the
    finger lifting returns it. Every custom property is the same one the mouse
    path writes, so the components need no knowledge of which input they got and
    there is no second code path to keep in sync.

    The listeners are passive: this must never interfere with scrolling a rail.
  */
  /*
    THE TOUCH RESPONSE HAS TO OUTLIVE THE FINGER.

    Every pointer response in this build is written for :hover, and the coarse
    path below answers it by setting data-gd-touched while a finger is down.
    That was enough for a state — a lift, a border, a brighter surface — and
    wrong for everything else, because most of these responses are one-shot
    animations of 600 to 900ms and a tap lasts about eighty. The sheen started
    and was cut off mid-pass; the crate began to open and slammed shut. On a
    phone the effects existed and were never actually seen.

    So the attribute now lingers. It goes on at touchstart and comes off a beat
    after the finger lifts — long enough for the longest one-shot in the build
    to finish, short enough that nothing is left looking stuck. A second touch
    cancels the pending removal rather than queueing another.

    This is the whole mechanism behind "the mobile version should animate like
    the desktop one". It is one timer, shared by every component that opts in.
  */
  const TOUCH_DWELL = 950;
  const touchTimers = new WeakMap();

  function touchOn(el) {
    window.clearTimeout(touchTimers.get(el));
    el.setAttribute('data-gd-touched', '');
  }

  function touchOff(el) {
    window.clearTimeout(touchTimers.get(el));
    touchTimers.set(
      el,
      window.setTimeout(function () {
        el.removeAttribute('data-gd-touched');
      }, TOUCH_DWELL)
    );
  }

  /*
    THE OPT-IN, FOR ANYTHING THAT WANTS THE TOUCH STATE WITHOUT THE TILT.

    data-gd-tilt and data-gd-sheen both imply a transform or a published
    pointer position, and most of what needs a touch response wants neither —
    a crate that opens, a seal that catches the light, an action that charges.
    data-gd-touch is that response on its own.

    Passive listeners, always: none of this may interfere with a scroll.
  */
  function bindTouch(scope) {
    if (!motionAllowed() || pointerQuery.matches) return;

    scope.querySelectorAll('[data-gd-touch]').forEach(function (el) {
      if (el.dataset.gdTouchBound === 'true') return;
      el.dataset.gdTouchBound = 'true';

      el.addEventListener(
        'touchstart',
        function () {
          touchOn(el);
        },
        { passive: true }
      );

      el.addEventListener(
        'touchend',
        function () {
          touchOff(el);
        },
        { passive: true }
      );

      el.addEventListener(
        'touchcancel',
        function () {
          touchOff(el);
        },
        { passive: true }
      );
    });
  }

  function bindTilt(scope) {
    if (!motionAllowed()) return;

    const coarse = !pointerQuery.matches;

    scope.querySelectorAll('[data-gd-tilt], [data-gd-sheen]').forEach(function (el) {
      if (el.dataset.gdTiltBound === 'true') return;
      el.dataset.gdTiltBound = 'true';

      const wantsTilt = el.hasAttribute('data-gd-tilt');
      const wantsSheen = el.hasAttribute('data-gd-sheen');
      const max = Math.max(0.5, Math.min(6, parseFloat(el.getAttribute('data-gd-tilt')) || 2));

      let frame = null;
      let nextX = 0;
      let nextY = 0;
      let pointerX = 50;
      let pointerY = 50;

      function render() {
        frame = null;
        if (wantsTilt) {
          el.style.setProperty('--gd-tilt-x', nextX.toFixed(2) + 'deg');
          el.style.setProperty('--gd-tilt-y', nextY.toFixed(2) + 'deg');
        }
        if (wantsSheen) {
          el.style.setProperty('--gd-pointer-x', pointerX.toFixed(1) + '%');
          el.style.setProperty('--gd-pointer-y', pointerY.toFixed(1) + '%');
        }
      }

      function schedule() {
        if (frame === null) frame = window.requestAnimationFrame(render);
      }

      /* Only elements that have not taken the transform for themselves get the
         engine's own rule. A component publishing its own transform — the entry
         packs compose tilt, lift and scale together — opts out by styling
         [data-gd-tilt] directly. */
      if (wantsTilt && !el.hasAttribute('data-gd-tilt-self')) el.classList.add('gd-tilt');

      function track(clientX, clientY) {
        const rect = el.getBoundingClientRect();
        if (!rect.width || !rect.height) return;

        const nx = (clientX - rect.left) / rect.width;
        const ny = (clientY - rect.top) / rect.height;

        nextX = (0.5 - ny) * 2 * max * 0.75;
        nextY = (nx - 0.5) * 2 * max;
        pointerX = nx * 100;
        pointerY = ny * 100;
        schedule();
      }

      /* Home, smoothly. The transition on the element does the easing; this
         only has to stop publishing an angle. The highlight is returned to the
         centre so it fades out from where it is rather than jumping there. */
      function release() {
        nextX = 0;
        nextY = 0;
        pointerX = 50;
        pointerY = 50;

        /* The angles home immediately; the state lingers, so a sheen that started
           under the finger is allowed to finish. */
        touchOff(el);
        schedule();
      }

      if (coarse) {
        el.addEventListener(
          'touchstart',
          function (event) {
            const t = event.touches[0];
            if (!t) return;
            /* The attribute is what the stylesheet hangs the lift and the
               highlight on, since :hover never fires here. */
            touchOn(el);
            track(t.clientX, t.clientY);
          },
          { passive: true }
        );

        el.addEventListener(
          'touchmove',
          function (event) {
            const t = event.touches[0];
            if (t) track(t.clientX, t.clientY);
          },
          { passive: true }
        );

        el.addEventListener('touchend', release, { passive: true });
        el.addEventListener('touchcancel', release, { passive: true });
      } else {
        el.addEventListener(
          'pointermove',
          function (event) {
            track(event.clientX, event.clientY);
          },
          { passive: true }
        );

        el.addEventListener('pointerleave', release);
      }
    });
  }

  /* Wiring ------------------------------------------------------------------ */

  function init(scope) {
    const root = scope || document;
    bindReveals(root);
    bindParallax(root);
    bindTilt(root);
    bindTouch(root);
  }

  function start() {
    init(document);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }

  /*
    The theme editor swaps a section's markup in place. The replaced nodes carry
    no bound markers, so binding the new subtree is enough — and the markers
    stop the untouched rest of the page being bound twice.
  */
  document.addEventListener('shopify:section:load', function (event) {
    init(event.target);
  });

  document.addEventListener('shopify:section:unload', function (event) {
    /* Drop parallax entries belonging to the removed subtree so the loop does
       not keep measuring detached nodes. */
    for (let i = parallaxItems.length - 1; i >= 0; i -= 1) {
      if (!document.contains(parallaxItems[i].el)) {
        if (parallaxObserver) parallaxObserver.unobserve(parallaxItems[i].el);
        parallaxItems.splice(i, 1);
      }
    }
  });

  /* Selecting a block in the editor scrolls it into view without a scroll
     event, so anything still hidden inside it is revealed directly. */
  document.addEventListener('shopify:block:select', function (event) {
    event.target.querySelectorAll('[data-gd-reveal]').forEach(markRevealed);
    if (event.target.hasAttribute('data-gd-reveal')) markRevealed(event.target);
  });

  /* A visitor turning reduced motion on mid-session gets the finished page
     rather than whatever frame the animation happened to be on. */
  reduceQuery.addEventListener('change', function () {
    if (!reduceQuery.matches) return;
    ROOT.classList.remove('gd-motion');
    pending.clear();
    document.querySelectorAll('[data-gd-reveal], [data-gd-border-draw]').forEach(markRevealed);
  });

  window.GemdrawReveal = { init: init };
})();
